import React, { useMemo, useState } from 'react';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import RetryIcon from '@material-ui/icons/Replay';
import RestoreIcon from '@material-ui/icons/Restore';
import GoogleIcon from '@material-ui/icons/CloudCircle';
import SyncIcon from '@material-ui/icons/Sync';
import InfoIcon from '@material-ui/icons/Info';
import { useWorkflowRuns, WorkflowRun } from '../../useWorkflowRuns';
import { WorkflowRunStatus } from '../WorkflowRunStatus';
import { useProjectName } from '../../useProjectName';
import { Entity } from '@backstage/catalog-model';
import { buildRouteRef } from '../../../routes';
import { DateTime } from 'luxon';
import { Table, TableColumn, Link } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import { getLocation } from '../../useLocation';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@material-ui/core';
import {
  getCloudbuildFilter,
  getRepoAnnotation,
} from '../../useCloudBuildFilter';
import { CloudbuildRollbackDrawer } from '../CloudbuildRollbackDrawer';
import { Alert } from '@material-ui/lab';

type Props = {
  runs?: WorkflowRun[];
  loading: boolean;
  total: number;
  error?: Error;
  isRerunning: Record<string, boolean>;
  location: string;
  repoAnnotation?: string;
  retry: () => void;
  projectName: string;
  openRollbackDrawer: () => void;
};

export const WorkflowRunsTableView = ({
  projectName,
  location,
  repoAnnotation,
  loading,
  retry,
  runs,
  total,
  isRerunning,
  openRollbackDrawer,
}: Props) => {
  const [infoOpen, setInfoOpen] = React.useState(false);
  const generatedColumns: TableColumn[] = useMemo(
    () => [
      {
        title: 'Status',
        width: '150px',
        type: 'string',
        field: 'status',
        searchable: true,
        render: (row: Partial<WorkflowRun>) => (
          <Box display="flex" alignItems="center">
            <WorkflowRunStatus status={row.status} />
          </Box>
        ),
      },
      {
        title: 'Build',
        field: 'id',
        type: 'string',
        width: '150px',
        searchable: true,
        render: (row: Partial<WorkflowRun>) => {
          const LinkWrapper = () => {
            const routeLink = useRouteRef(buildRouteRef);
            return (
              <Link data-testid="cell-source" to={routeLink({ id: row.id! })}>
                {row.id?.substring(0, 8)}
              </Link>
            );
          };

          return <LinkWrapper />;
        },
      },
      {
        title: 'Trigger Name',
        field: 'substitutions.TRIGGER_NAME',
        type: 'string',
        searchable: true,
        render: (row: Partial<WorkflowRun>) => (
          <Typography variant="body2" noWrap>
            {row.substitutions?.TRIGGER_NAME}
          </Typography>
        ),
      },
      {
        title: 'Source',
        field: 'message',
        type: 'string',
        highlight: true,
        width: '200px',
        searchable: true,
        render: (row: Partial<WorkflowRun>) => (
          <Typography variant="body2" noWrap>
            {row.message}
          </Typography>
        ),
      },
      {
        title: 'Ref',
        field: 'substitutions.REF_NAME',
        type: 'string',
        searchable: true,
        render: (row: Partial<WorkflowRun>) => (
          <Typography variant="body2" noWrap>
            {row.substitutions?.REF_NAME}
          </Typography>
        ),
      },
      {
        title: 'Commit',
        field: 'substitutions.SHORT_SHA',
        type: 'string',
        searchable: true,
        render: (row: Partial<WorkflowRun>) => (
          <Typography variant="body2" noWrap>
            {row.substitutions?.SHORT_SHA}
          </Typography>
        ),
      },
      {
        title: 'Created',
        field: 'createTime',
        type: 'datetime',
        render: (row: Partial<WorkflowRun>) => {
          if (!row.createTime) return null;

          const formattedDate = row.createTime.includes('-')
            ? DateTime.fromISO(row.createTime).toFormat('dd-MM-yyyy HH:mm:ss')
            : DateTime.fromSeconds(parseInt(row.createTime, 10)).toFormat(
                'dd-MM-yyyy HH:mm:ss',
              );

          return (
            <Typography data-testid="cell-created" variant="body2" noWrap>
              {formattedDate}
            </Typography>
          );
        },
      },
      {
        title: 'Duration',
        field: 'duration',
        type: 'string',
      },
      {
        title: 'Actions',
        render: (row: Partial<WorkflowRun>) => (
          <Tooltip
            title={isRerunning[row.id!] ? 'Rerunning...' : 'Rerun workflow'}
          >
            <IconButton
              onClick={() => row.rerun?.()}
              disabled={isRerunning[row.id!]}
              data-testid="action-rerun"
            >
              <RetryIcon
                className={isRerunning[row.id!] ? 'animate-spin' : ''}
              />
            </IconButton>
          </Tooltip>
        ),
        width: '10%',
      },
    ],
    [isRerunning],
  );
  return (
    <>
      <Table
        isLoading={loading}
        options={{ paging: true, padding: 'dense' }}
        totalCount={total}
        actions={[
          {
            icon: () => <RestoreIcon />,
            tooltip: 'Rollback deployment',
            isFreeAction: true,
            onClick: () => {
              openRollbackDrawer();
            },
          },
          {
            icon: () => <SyncIcon />,
            tooltip: 'Reload workflow runs',
            isFreeAction: true,
            onClick: () => retry(),
          },
          {
            icon: () => <InfoIcon />,
            tooltip: 'More Info',
            isFreeAction: true,
            onClick: () => setInfoOpen(true),
          },
        ]}
        data={runs ?? []}
        style={{ width: '100%' }}
        title={
          <Box display="flex" alignItems="center">
            <GoogleIcon />
            <Box mr={1} />
            <Box mr={1} />
            <Typography variant="h6">
              {projectName} / {location} / {repoAnnotation}
            </Typography>
          </Box>
        }
        columns={generatedColumns}
      />
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} keepMounted>
        <DialogTitle>Information</DialogTitle>
        <DialogContent>
          <Typography>
            This table displays the most recent 500 workflow runs for the
            selected project.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const WorkflowRunsTable = (props: { entity: Entity }) => {
  const { value: projectName, loading: projectLoading } = useProjectName(
    props.entity,
  );
  const [projectId] = (projectName ?? '/').split('/');
  const location = getLocation(props.entity);
  const cloudBuildFilter = getCloudbuildFilter(props.entity);
  const repoAnnotation = getRepoAnnotation(props.entity);

  // Rollback Drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const openRollbackDrawer = () => {
    setDrawerOpen(true);
  };
  const handleClose = () => {
    setDrawerOpen(false);
  };

  const [{ runs, loading: runsLoading, total, error, isRerunning }, { retry }] =
    useWorkflowRuns({
      projectId,
      location,
      cloudBuildFilter,
    });

  if (error) {
    return <Alert severity="error">Error: {error.message}</Alert>;
  }

  return (
    <>
      <WorkflowRunsTableView
        projectName={projectName ?? ''}
        runs={runs}
        loading={projectLoading || runsLoading}
        total={total}
        error={error}
        isRerunning={isRerunning}
        location={location}
        repoAnnotation={repoAnnotation}
        retry={retry}
        openRollbackDrawer={openRollbackDrawer}
      />

      <CloudbuildRollbackDrawer
        open={drawerOpen}
        onClose={handleClose}
        runs={runs}
        projectId={projectId}
        location={location}
      />
    </>
  );
};
